/**
 * Staff PIN length rules.
 *
 * Shared by the User Information control (pages/UserSettings.js) and the login
 * PIN pad (components/UserSelectionModal.js) so the two can never disagree about
 * how many boxes a given user has to fill.
 *
 * Regular users' PIN length is store-configurable; admins are fixed at
 * ADMIN_PIN_LENGTH no matter what the store setting says, so an admin PIN is
 * always a known width even while the setting is mid-change or unreadable.
 *
 * "Admin" here means the user's own authLevel, NOT the Firebase `admin` custom
 * claim: every staff member signs in through the same Firebase account, so that
 * claim is identical for everyone and cannot tell roles apart. authLevel is the
 * per-person role this app actually stores.
 *
 * Nothing here covers booking PINs (components/PinAuthLayer.js) — those are
 * customer-facing, verified server-side, and unrelated.
 */

export const MIN_PIN_LENGTH = 2;
export const MAX_PIN_LENGTH = 10;

/** What every pre-existing user's PIN was created at. */
export const DEFAULT_PIN_LENGTH = 3;

export const ADMIN_PIN_LENGTH = 4;
export const ADMIN_AUTH_LEVEL = 5;

/**
 * Threshold for the "(admin)" badge in the header — deliberately LOWER than
 * ADMIN_AUTH_LEVEL. Level 4 reads as admin to staff but does not get the
 * fixed-width admin PIN or the PIN-length control, both of which need 5.
 */
export const ADMIN_BADGE_AUTH_LEVEL = 4;

export const isAdminBadgeLevel = (authLevel) =>
  Number(authLevel) >= ADMIN_BADGE_AUTH_LEVEL;

/** settings/<doc>.<field> holding the store-wide length for non-admins. */
export const PIN_LENGTH_SETTING_DOC = "security";
export const PIN_LENGTH_SETTING_FIELD = "pinLength";

export const clampPinLength = (value) => {
  const n = Math.round(Number(value));
  if (!Number.isFinite(n)) return DEFAULT_PIN_LENGTH;
  return Math.min(MAX_PIN_LENGTH, Math.max(MIN_PIN_LENGTH, n));
};

export const isAdminAuthLevel = (authLevel) =>
  Number(authLevel) >= ADMIN_AUTH_LEVEL;

export const isAdminUser = (user) => isAdminAuthLevel(user?.authLevel);

/**
 * @param {number|string} authLevel  role of the user whose PIN this is
 * @param {number} storePinLength    store-wide setting, used for non-admins
 */
export const pinLengthForAuthLevel = (authLevel, storePinLength) =>
  isAdminAuthLevel(authLevel) ? ADMIN_PIN_LENGTH : clampPinLength(storePinLength);

export const pinLengthForUser = (user, storePinLength) =>
  pinLengthForAuthLevel(user?.authLevel, storePinLength);

/**
 * PINs were historically written to Firestore as numbers, which silently ate
 * leading zeros — "012" was stored as 12 and then never matched at login. Pad
 * both sides to the expected width before comparing or displaying so old numeric
 * values and new string values behave identically.
 *
 * Deliberately pads but never truncates: a stored PIN longer than the current
 * length is wrong for this store and should fail to match, not be trimmed into
 * something that accidentally does.
 */
export const normalizePin = (value, length) => {
  if (value === null || value === undefined || value === "") return "";
  const digits = String(value).replace(/\D/g, "");
  if (!digits) return "";
  return digits.padStart(length, "0");
};
